import type { DependencyList } from 'react';
import { useCallback, useEffect, useRef } from 'react';
// noinspection ES6PreferShortImport
import { useCacheContext } from '../context';

type FuncType<T extends unknown[], U> = (...args: T) => U;

export const useCacheCallback = <T extends unknown[], R>(
  callback: FuncType<T, Promise<R>>,
  options: {
    /**
     * must generate a globally unique cache key
     */
    getCacheKey: FuncType<T, string>;
    /**
     * time in ms, default to 5 minute
     */
    cacheTime?: number;
  },
  deps: DependencyList,
) => {
  const { setValue, getValue } = useCacheContext();
  const refOptions = useRef(options);
  useEffect(() => {
    refOptions.current = options;
  }, [options]);

  return useCallback(
    (...args: T): Promise<R> => {
      const { getCacheKey, cacheTime = 1000 * 60 * 5 } = refOptions.current;

      const key = getCacheKey(...args);
      let promise = getValue<Promise<R>>(key);
      if (!promise) {
        promise = callback(...args);
        setValue(key, promise, { ttl: cacheTime });
      }
      return promise;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [callback, ...deps],
  );
};
